import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Tab as TabType } from '@yleisradio/areena-types';
import { getContentCardForSlot } from 'components/BrazeContentCard';
import { Container } from 'components/Container';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { HighlightCard } from 'components/HighlightCard';
import { List } from 'components/List';
import { Notification } from 'components/Notifications/Notification';
import { VideoFeedCarousel } from 'components/VideoFeedCarousel';
import { useBrazeSdk } from 'contexts/BrazeSdkContext';
import { useTrackSnowplowPageView } from 'hooks/useTrackSnowplowPageView';
import { useTranslation } from 'hooks/useTranslation';
import dynamic from 'next/dynamic';
import React from 'react';
import { createHighlightCardKey } from 'utils/content';

const BrazeContentCard = dynamic(
  () =>
    import('components/BrazeContentCard').then((mod) => mod.BrazeContentCard),
  { ssr: false }
);

interface TabContentProps {
  tab: TabType;
}

export const TabContent: React.FunctionComponent<TabContentProps> = ({
  tab: { analytics, content: contentArray = [], title, slug },
}) => {
  useTrackSnowplowPageView(analytics, slug);

  const { contentCards } = useBrazeSdk();
  const t = useTranslation();

  const firstContentIsListWithoutTitle =
    contentArray[0]?.type === 'list' && !contentArray[0].title;

  const errorFallback = (
    <Container>
      <Notification
        notification={{ uiMessage: t('listLoadingFailed') }}
        notificationStyle="error"
        helpText={t('notificationHelpTextGeneric')}
      />
    </Container>
  );

  return (
    <div data-testid="tab-content">
      {firstContentIsListWithoutTitle && title ? (
        <VisuallyHidden asChild>
          <h2>{title}</h2>
        </VisuallyHidden>
      ) : null}

      {contentArray.map((content, index) => {
        switch (content.type) {
          case 'contentCard': {
            const contentCard = getContentCardForSlot(
              content,
              contentArray,
              contentCards
            );

            return contentCard ? (
              <ErrorBoundary key={contentCard.id}>
                <BrazeContentCard card={contentCard} />
              </ErrorBoundary>
            ) : null;
          }

          case 'highlightCard': {
            const key = createHighlightCardKey(content, index);

            return (
              <ErrorBoundary key={key}>
                <HighlightCard
                  card={content}
                  cardKey={key}
                  contentIndex={index}
                />
              </ErrorBoundary>
            );
          }

          case 'list': {
            const key = `${content.source.uri}-${index}`;

            return (
              <ErrorBoundary fallback={errorFallback} key={key}>
                <List
                  index={index}
                  list={content}
                  listKey={key}
                  tabTitle={title}
                />
              </ErrorBoundary>
            );
          }

          case 'html': {
            if (content.source.uri.startsWith('videofeedcarousel://')) {
              const key = `${content.source.uri}-${index}`;

              return (
                <ErrorBoundary fallback={errorFallback} key={key}>
                  <VideoFeedCarousel content={content} contentIndex={index} />
                </ErrorBoundary>
              );
            }

            // no other HTML content supported
            return null;
          }

          default: {
            return null;
          }
        }
      })}
    </div>
  );
};
